import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  DollarSign, Users, TrendingUp, PieChart, BarChart3, 
  Calendar, RefreshCw, Loader2, AlertCircle, ArrowUpRight,
  CreditCard, Target, Percent
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const AdminRevenueDashboard = () => {
  const { apiCall } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('30');

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiCall(`/api/revenue-analytics/overview?days=${period}`);
      setData(response);
    } catch (err) {
      setError(err.message || 'Failed to load analytics');
      console.error('Revenue analytics error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [period]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-6 w-6 text-red-600" />
                <div>
                  <h3 className="font-semibold text-red-800">Error Loading Analytics</h3>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
              <Button onClick={fetchData} className="mt-4" variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Prepare chart data
  const pieData = data?.vendor_distribution?.map(d => ({
    name: d.tier_name,
    value: d.count,
    percentage: d.percentage
  })) || [];

  const barData = data?.revenue_breakdown?.map(d => ({
    name: d.source,
    amount: d.amount,
    count: d.count
  })) || [];

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Revenue Analytics</h1>
            <p className="text-sm text-gray-500">Financial overview and subscription metrics</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Select period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="15">Last 15 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* MRR Card */}
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm font-medium">Monthly Recurring Revenue</p>
                  <p className="text-3xl font-bold mt-1">{formatCurrency(data?.total_mrr)}</p>
                  <p className="text-blue-200 text-xs mt-1">From active subscriptions</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-400/30 flex items-center justify-center">
                  <DollarSign className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Active Vendors Card */}
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm font-medium">Total Active Vendors</p>
                  <p className="text-3xl font-bold mt-1">{data?.total_active_vendors || 0}</p>
                  <p className="text-green-200 text-xs mt-1">Across all tiers</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-green-400/30 flex items-center justify-center">
                  <Users className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ad Credit Usage Card */}
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm font-medium">Ad Credit Usage</p>
                  <p className="text-3xl font-bold mt-1">{data?.ad_credit_usage_percent || 0}%</p>
                  <p className="text-amber-200 text-xs mt-1">Of total credits purchased</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-amber-400/30 flex items-center justify-center">
                  <Percent className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Vendors by Tier Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="h-5 w-5 text-blue-600" />
                Vendors by Tier
              </CardTitle>
              <CardDescription>Distribution of vendors across subscription tiers</CardDescription>
            </CardHeader>
            <CardContent>
              {pieData.length > 0 ? (
                <div className="flex items-center gap-4">
                  <div className="w-1/2 h-64">
                    <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                      <RechartsPie>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          dataKey="value"
                          label={({ name, percentage }) => `${percentage}%`}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </div>
                  <div className="w-1/2 space-y-2">
                    {pieData.map((item, index) => (
                      <div key={item.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <span className="text-sm text-gray-700">{item.name}</span>
                        </div>
                        <span className="text-sm font-medium">{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  No vendor data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Revenue Source Bar Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-green-600" />
                Revenue Source Split
              </CardTitle>
              <CardDescription>Revenue breakdown by source type</CardDescription>
            </CardHeader>
            <CardContent>
              {barData.length > 0 ? (
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                    <BarChart data={barData}>
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        labelStyle={{ fontWeight: 'bold' }}
                      />
                      <Bar dataKey="amount" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  No revenue data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Renewals Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-purple-600" />
              Upcoming Renewals
            </CardTitle>
            <CardDescription>Vendor subscriptions expiring in the next 30 days</CardDescription>
          </CardHeader>
          <CardContent>
            {data?.upcoming_renewals?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Vendor</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Plan</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Expiry Date</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Expected Revenue</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-gray-500">Days Left</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.upcoming_renewals.map((renewal, index) => (
                      <tr key={renewal.vendor_id || index} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-gray-900">{renewal.vendor_name}</p>
                            <p className="text-xs text-gray-500">{renewal.vendor_email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">{renewal.plan_name}</Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {formatDate(renewal.expiry_date)}
                        </td>
                        <td className="py-3 px-4 text-right font-medium">
                          {formatCurrency(renewal.expected_revenue)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <Badge 
                            variant={renewal.days_until_expiry <= 7 ? 'destructive' : renewal.days_until_expiry <= 14 ? 'warning' : 'default'}
                            className={renewal.days_until_expiry <= 7 ? 'bg-red-100 text-red-800' : renewal.days_until_expiry <= 14 ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}
                          >
                            {renewal.days_until_expiry} days
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-8 text-center text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No upcoming renewals in the next 30 days</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Revenue Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {data?.revenue_breakdown?.map((item, index) => (
            <Card key={item.source}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div 
                    className="h-10 w-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${COLORS[index % COLORS.length]}20` }}
                  >
                    {item.source === 'Subscriptions' && <CreditCard className="h-5 w-5" style={{ color: COLORS[index % COLORS.length] }} />}
                    {item.source === 'Ad Credits' && <Target className="h-5 w-5" style={{ color: COLORS[index % COLORS.length] }} />}
                    {item.source === 'Lead Unlocks' && <Users className="h-5 w-5" style={{ color: COLORS[index % COLORS.length] }} />}
                    {item.source === 'Commissions' && <TrendingUp className="h-5 w-5" style={{ color: COLORS[index % COLORS.length] }} />}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{item.source}</p>
                    <p className="font-semibold">{formatCurrency(item.amount)}</p>
                    <p className="text-xs text-gray-400">{item.count} transactions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminRevenueDashboard;
